/* eslint-disable react/jsx-handler-names */
import React from 'react';
import classnames from 'classnames';
import {withRouter} from 'react-router-dom';
import queryString from 'query-string';

import Icon from 'eventbrite_design_system/icon/Icon';
import Button from 'eventbrite_design_system/button/Button';
import DateRangePicker from 'eventbrite_design_system/datePicker/DateRangePicker';
import ChevronDownChunkySvg from 'eventbrite_design_system/iconography/icons/ChevronDownChunky';
import ChevronUpChunkySvg from 'eventbrite_design_system/iconography/icons/ChevronUpChunky';
import {ISO_8601_DATE_FORMAT} from 'eventbrite_design_system/utils/date';
import moment from 'js-utils/moment';
import differenceBy from 'lodash/differenceBy';

import {gettext} from '../../translations';
import {START_DATE, CUSTOM_DATE} from '../constants';
import {requestAnimationFrameWithFallback} from '../utils';
import DateRangePickerSearchContainer from './DateRangePickerSearchContainer';

import './FilterSection.scss';

const DATEPICKER_DEFAULT_DISPLAY_FORMAT = 'MMM D';

const isFirstValueBelowTheFold = (value, croppedRadioValues) => value === croppedRadioValues[0].value;

// eslint-disable-next-line react/no-multi-comp
class AnimatedFilters extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            shownFilters: props.croppedFilters,
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.expand !== this.props.expand) {
            this.updateFilters();
        }
    }

    updateFilters = () => {
        this.setState((state, props) => {
            if (props.expand) {
                // When expanding, we want to add filters one at a time, using requestAnimationFrame to
                // schedule the updates
                const difference = differenceBy(props.allFilters, state.shownFilters, ({value}) => value);

                if (difference.length > 0) {
                    const nextShownFilters = state.shownFilters.concat(difference[0]);

                    requestAnimationFrameWithFallback(this.updateFilters);
                    return {shownFilters: nextShownFilters};
                }
            } else if (state.shownFilters.length > props.croppedFilters.length) {
                // Handle collapsing, by removing one filter at a time until we reach the expected length
                let nextShownFilters = state.shownFilters.slice(0, state.shownFilters.length - 1);

                if (nextShownFilters.find(({value}) => value === props.shownFilters)) {
                    nextShownFilters.concat(props.allFilters.find(({value}) => value === props.shownFilters));
                }

                requestAnimationFrameWithFallback(this.updateFilters);
                return {shownFilters: nextShownFilters};
            }

            return null;
        });
    }

    render() {
        let filtersToShow = this.state.shownFilters;

        if (!filtersToShow.find(({value}) => value === this.props.selectedFilter)) {
            filtersToShow = [...filtersToShow, this.props.allFilters.find(({value}) => value === this.props.selectedFilter)];
        }

        return this.props.renderRadios(filtersToShow);
    }
}

// eslint-disable-next-line react/no-multi-comp
class FilterSection extends React.Component {
    state = {
        // the currently selected filter in this section
        selectedFilter: Object.keys(this.props.filters)[0],

        // show the whole filter section (vs collapsing part of it)
        expand: false,
    }

    componentDidMount() {
        // when the page is loaded for the first time, we check the url to see whether
        // a filter should be set in the radio buttons
        this._parseUrl(this.props.location, false);
    }

    componentDidUpdate(prevProps) {
        // if the user clicks the back or forward button, we check the new url to see
        // if a filter needs to be applied
        if (this.props.location !== prevProps.location) {
            this._parseUrl(this.props.location, true);
        }
    }

    /**
     * Examine the url and apply any filters that are present in the url
     *
     * @param {string} url - the url of this page
     * @param {bool} shouldSearch - should we make an ajax search request to the server
     *
     */
    _parseUrl = (location, shouldSearch) => {
        const {urlParameterMapping, isDateSection} = this.props;

        // we special-case the custom date search filter by looking for the start_date param
        if (isDateSection && location.search.includes(START_DATE)) {
            this._handleOptionChange(CUSTOM_DATE, shouldSearch, false);
            return;
        }

        const url = location.pathname;

        if (urlParameterMapping) {
            const urlSplit = url.split('/');

            if (urlSplit.length > 3) {
                // individual filters are separated by double dashes
                const urlParams = urlSplit[3].split('--');

                // check every possible filter to see whether it exists in this url
                for (const key in urlParameterMapping) {
                    if (urlParams.includes(key)) {
                        this._handleOptionChange(urlParameterMapping[key], shouldSearch, false);

                        // only one filter at a time so return when we find one
                        return;
                    }
                }

                // if we can't find any filters in the url, we apply the 'all' filter
                this._handleOptionChange('all', shouldSearch, false);
            }
        }
    }

    /**
     * Handle a change of the filter setting. This is triggered in three cases.
     *
     * 1. The initial page load. We want to set the radio value but not trigger a new search
     * because the server has already included the search results in the html.
     *
     * 2. The back or forward button is pressed. We want to set the radio button and trigger
     * a new search but we don't want to update the url path location.
     *
     * 3. The user has selected a new radio option.  We want to set the radio button, trigger
     * a new search, and update the url path to represent the new option state.
     *
     * @param {string} value - the new selected value to be set for the radio buttons
     * @param {bool} shouldSearch - should we make an ajax search request to the server
     * @param {bool} shouldUpdateRoute - should we set a new location in the url
     *
     */
    _handleOptionChange = (value, shouldSearch, shouldUpdateRoute) => {
        const {onOptionChange} = this.props;

        if (value !== this.state.selectedFilter) {
            this.setState({selectedFilter: value});
            if (shouldSearch && value !== CUSTOM_DATE) {
                onOptionChange(value, shouldUpdateRoute);
            }
        }
    };

    /**
     * Toggle the expand state specifying whether the whole filter section should be shown
     */
    _handleToggleExpand = () => {
        this.setState((prevState) => ({expand: !prevState.expand}));
    }

    _handleDatePickerBlur = ({startDate, endDate}) => {
        const {onCustomDate} = this.props;

        if (startDate && endDate) {
            onCustomDate(startDate, endDate);
        }
    }

    render() {
        const {
            // the text title of this section of filters
            title,

            // the filter options to be presented to a user
            filters,

            // the text to present on the more button.  e.g. "More categories"
            moreLabel,

            // the text to present on the less button when filters are expanded.  e.g. "Fewer categories"
            lessLabel,

            // the number of options to present before we hide remaining options behind a "more" button
            moreThreshold,

            // the window location provided by withRouter
            location,
        } = this.props;

        const urlParams = queryString.parse(location.search.replace('?', ''));

        // if we're not in an "expand" state, we only show the first x filters (x = moreThreshold)
        const entries = Object.entries(filters);
        const filtersCropped = entries.slice(0, moreThreshold);
        const filterCroppedRemaining = entries.slice(moreThreshold, entries.length);

        const croppedRadioValues = filtersCropped.map(([key, label]) => ({
            display: label,
            value: key,
        }));
        const croppedRemainingRadioValues = filterCroppedRemaining.map(([key, label]) => ({
            display: label,
            value: key,
        }));
        const allRadioValues = [
            ...croppedRadioValues,
            ...croppedRemainingRadioValues,
        ];

        let buttonLabel = moreLabel;

        let buttonAriaLabel = gettext('Show more options');

        let buttonIconType = <ChevronDownChunkySvg />;

        if (this.state.expand) {
            buttonLabel = lessLabel;
            buttonAriaLabel = gettext('Show fewer options');
            buttonIconType = <ChevronUpChunkySvg />;
        }

        return (
            <div className="eds-text-bm">
                <Button
                    type="button"
                    style="none"
                    disabled={!moreThreshold}
                    onClick={this._handleToggleExpand}
                    aria-label={gettext('Expand or collapse option list')}
                    __containerClassName="filter-section__title"
                >
                    {title}
                    {
                        (allRadioValues.length >= moreThreshold) &&
                        <span className="eds-l-mar-left-2">
                            <Icon type={buttonIconType} color="grey-700" />
                        </span>
                    }
                </Button>
                <AnimatedFilters
                    allFilters={allRadioValues}
                    croppedFilters={croppedRadioValues}
                    expand={this.state.expand}
                    selectedFilter={this.state.selectedFilter}
                    moreThreshold={moreThreshold}
                    renderRadios={
                        (radios) => radios.map(
                            ({display, value}) => (
                                <div key={value}>
                                    {
                                        // Show a border when expanded to separate "above the fold" and "below the fold"
                                        this.state.selectedFilter !== CUSTOM_DATE && isFirstValueBelowTheFold(value, croppedRemainingRadioValues) &&
                                        <div className="filter-section__filter-divider" />
                                    }
                                    <Button
                                        aria-label={display}
                                        style="none"
                                        __containerClassName={
                                            classnames(
                                                'filter-section__button',
                                                `filter-section__button-${this.state.selectedFilter === value ? 'selected' : 'unselected'}`,
                                            )
                                        }
                                        onClick={() => this._handleOptionChange(value, true, true)}
                                    >
                                        <span>
                                            {display}
                                        </span>
                                    </Button>
                                    {value === CUSTOM_DATE && this.state.selectedFilter === CUSTOM_DATE &&
                                        <DateRangePickerSearchContainer>
                                            <DateRangePicker
                                                onBlur={this._handleDatePickerBlur}
                                                inputDisplayFormat={DATEPICKER_DEFAULT_DISPLAY_FORMAT}
                                                minDate={moment().format(ISO_8601_DATE_FORMAT)}
                                                defaultValue={{startDate: urlParams.start_date, endDate: urlParams.end_date}}
                                            />
                                        </DateRangePickerSearchContainer>
                                    }
                                </div>
                            )
                        )
                    }
                />
                {
                    moreThreshold &&
                        <Button
                            type="button"
                            style="none"
                            onClick={this._handleToggleExpand}
                            aria-label={buttonAriaLabel}
                            __containerClassName="filter-section__more eds-text-weight--heavy"
                        >
                            {buttonLabel}
                        </Button>
                }
            </div>);
    }
}

// withRouter adds a location field to props which allows us to watch for url changes
export default withRouter(FilterSection);
